import { ExperimentType, type CreateExperimentRequest } from "@/http/api-types";
import type { ExperimentFormValues } from "./ExperimentForm";

export type ExperimentFormFieldErrors = Record<string, string>;

type SaveValidationContext = {
  organizationIds: number[];
  validCampaignIds: Set<string>;
};

type BackendValidationError = {
  field: string;
  message: string;
};

type ErrorWithBackendValidation = {
  response?: {
    data?: {
      errors?: BackendValidationError[];
    };
  };
};

export function isValidUrl(url: string): boolean {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

// Date pickers hand back local-midnight Date values, while experiment dates are
// stored as UTC instants. This is the inverse of combineDateAndTime: it puts the
// stored UTC calendar day on the local calendar so the picker shows the same day
// the rest of the dashboard renders, and so editing an experiment outside UTC
// does not shift its dates on save.
export function toUTCCalendarDate(value?: string): Date | null {
  if (!value) return null;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
}

export function combineDateAndTime(date: Date, time: string, isEndTime: boolean = false): Date {
  const [hours, minutes] = time.split(":").map(Number);
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  if (isEndTime && hours === 23 && minutes === 59) {
    return new Date(Date.UTC(year, month, day, 23, 59, 59, 999));
  }

  return new Date(Date.UTC(year, month, day, hours, minutes, 0, 0));
}

export function validateExperimentFormValues(
  values: ExperimentFormValues,
  { organizationIds, validCampaignIds }: SaveValidationContext,
): ExperimentFormFieldErrors {
  const fieldErrors: ExperimentFormFieldErrors = {};

  if (!values.name?.trim()) fieldErrors.name = "Experiment name is required";
  if (!values.hypothesis?.trim()) fieldErrors.hypothesis = "Hypothesis is required";
  if (!values.owner) fieldErrors.owner = "Owner is required";
  if (!values.startDate) fieldErrors.start_date = "Start date is required";
  if (!values.endDate) fieldErrors.end_date = "End date is required";
  if (values.startDate && values.endDate && values.endDate < values.startDate) {
    fieldErrors.end_date = "End date must be after start date";
  }

  const invalidLinks = (values.externalLinks || []).filter(link => link.url && !isValidUrl(link.url));
  if (invalidLinks.length > 0) {
    fieldErrors.external_links = "All external links must be valid URLs";
  }

  if (values.experimentType === ExperimentType.Custom) {
    if (!values.audiences || values.audiences.length === 0) {
      fieldErrors.audiences = "At least one audience is required for custom experiments";
    }
    if (organizationIds.length === 0) {
      fieldErrors.organizations = "Could not determine an organization from the selected audiences";
    }

    const validCampaigns = (values.campaigns || []).filter(c => c.campaignId);
    if (validCampaigns.length < 2) {
      fieldErrors.campaigns = "Please select at least 2 campaigns";
    } else {
      const controlCampaignIds = new Set(validCampaigns.filter(c => c.variant === "control").map(c => c.campaignId));
      const testCampaigns = validCampaigns.filter(c => c.variant === "test");
      const hasTest = testCampaigns.length > 0;
      const hasControl = controlCampaignIds.size > 0;
      if (!hasTest || !hasControl) {
        fieldErrors.campaigns = "You must have at least one test campaign and one control campaign";
      }
      const uniqueCampaignIds = new Set(validCampaigns.map(c => c.campaignId));
      if (uniqueCampaignIds.size !== validCampaigns.length) {
        fieldErrors.campaigns = "Each campaign can only be selected once";
      }
      const outOfScopeCampaigns = validCampaigns.filter(c => !validCampaignIds.has(c.campaignId));
      if (outOfScopeCampaigns.length > 0) {
        fieldErrors.campaigns = "Each campaign must belong to one of the selected audiences";
      }
      const totalAllocation = validCampaigns.reduce((sum, c) => sum + (c.trafficAllocation || 0), 0);
      if (totalAllocation !== 10000) {
        fieldErrors.campaigns = `Traffic allocation must sum to 100%. Current: ${(totalAllocation / 100).toFixed(2)}%`;
      }
      if (validCampaigns.some(c => !c.trafficAllocation || c.trafficAllocation <= 0)) {
        fieldErrors.campaigns = "Each campaign must have a traffic allocation greater than 0%";
      }
      const invalidControlAssignments = validCampaigns.filter(c => c.variant === "control" && c.controlCampaignId);
      if (invalidControlAssignments.length > 0 && !fieldErrors.campaigns) {
        fieldErrors.campaigns = "Control campaigns cannot be assigned to another control campaign";
      }
      const unassignedTests = testCampaigns.filter(c => !c.controlCampaignId);
      if (unassignedTests.length > 0 && !fieldErrors.campaigns) {
        fieldErrors.campaigns = "Assign each test campaign to a control campaign";
      }
      const testsAssignedToNonControls = testCampaigns.filter(
        c => c.controlCampaignId && !controlCampaignIds.has(c.controlCampaignId),
      );
      if (testsAssignedToNonControls.length > 0 && !fieldErrors.campaigns) {
        fieldErrors.campaigns = "Each test campaign must be assigned to a selected control campaign";
      }
      const testCountsByControlId = new Map<string, number>();
      testCampaigns.forEach(c => {
        if (!c.controlCampaignId || !controlCampaignIds.has(c.controlCampaignId)) return;
        testCountsByControlId.set(c.controlCampaignId, (testCountsByControlId.get(c.controlCampaignId) || 0) + 1);
      });
      const controlsWithoutTests = Array.from(controlCampaignIds).filter(
        controlId => !testCountsByControlId.has(controlId),
      );
      if (controlsWithoutTests.length > 0 && !fieldErrors.campaigns) {
        fieldErrors.campaigns = "Each control campaign must have at least one assigned test campaign";
      }

      const testsWithoutGroup = testCampaigns.filter(c => !c.testGroup);
      if (testsWithoutGroup.length > 0 && !fieldErrors.campaigns) {
        fieldErrors.campaigns = "Assign each test campaign to a test group";
      }
      const seenControlGroup = new Set<string>();
      const groupsSeen = new Set<number>();
      let maxTestGroup = 0;
      let duplicateControlGroup = false;
      testCampaigns.forEach(c => {
        if (!c.testGroup) return;
        groupsSeen.add(c.testGroup);
        maxTestGroup = Math.max(maxTestGroup, c.testGroup);
        if (c.controlCampaignId) {
          const key = `${c.controlCampaignId}:${c.testGroup}`;
          if (seenControlGroup.has(key)) duplicateControlGroup = true;
          seenControlGroup.add(key);
        }
      });
      if (duplicateControlGroup && !fieldErrors.campaigns) {
        fieldErrors.campaigns = "Each control campaign can have at most one test campaign per test group";
      }
      for (let group = 1; group <= maxTestGroup; group++) {
        if (!groupsSeen.has(group) && !fieldErrors.campaigns) {
          fieldErrors.campaigns = "Test groups must be contiguous starting at 1";
          break;
        }
      }
    }
  } else {
    if (!values.importedGlobalExperimentId) {
      fieldErrors.imported_global_experiment_id = "Import a global experiment first";
    }
    const validPredictionFlights = (values.predictionFlights || []).filter(pf => pf.predictionFlightId);
    if (validPredictionFlights.length < 2) {
      fieldErrors.prediction_flights = "Please select at least 2 prediction flights";
    } else {
      const hasTest = validPredictionFlights.some(pf => pf.variant === "test");
      const hasControl = validPredictionFlights.some(pf => pf.variant === "control");
      if (!hasTest || !hasControl) {
        fieldErrors.prediction_flights =
          "You must have at least one test prediction flight and one control prediction flight";
      }
      const uniqueIds = new Set(validPredictionFlights.map(pf => pf.predictionFlightId));
      if (uniqueIds.size !== validPredictionFlights.length) {
        fieldErrors.prediction_flights = "Each prediction flight can only be selected once";
      }
      if (validPredictionFlights.some(pf => !pf.trafficAllocation || pf.trafficAllocation <= 0)) {
        fieldErrors.prediction_flights = "Each prediction flight must have a traffic allocation greater than 0%";
      }
    }
  }

  return fieldErrors;
}

export function buildExperimentRequest(
  values: ExperimentFormValues,
  { organizationIds }: Pick<SaveValidationContext, "organizationIds">,
): CreateExperimentRequest {
  const startDateTime = combineDateAndTime(values.startDate!, values.startTime, false);
  const endDateTime = combineDateAndTime(values.endDate!, values.endTime, true);

  const experimentData: CreateExperimentRequest = {
    name: values.name.trim(),
    type: values.experimentType,
    hypothesis: values.hypothesis.trim(),
    conclusion: values.conclusion.trim() || undefined,
    outcome: values.outcome || undefined,
    rollout_additional_info: values.rollOutAdditionalInfo.trim() || undefined,
    owner: parseInt(values.owner, 10),
    start_date: startDateTime.toISOString(),
    end_date: endDateTime.toISOString(),
    external_links: (values.externalLinks || []).map(link => link.url).filter(link => link.trim() !== ""),
  };

  if (values.experimentType === ExperimentType.Custom) {
    experimentData.organizations = organizationIds;
    experimentData.audiences = (values.audiences || []).map(id => parseInt(id, 10));
    experimentData.campaigns = (values.campaigns || [])
      .filter(c => c.campaignId)
      .map(c => ({
        campaign_id: parseInt(c.campaignId, 10),
        control_campaign_id:
          c.variant === "test" && c.controlCampaignId ? parseInt(c.controlCampaignId, 10) : undefined,
        test_group: c.variant === "test" && c.testGroup ? c.testGroup : undefined,
        variant: c.variant,
        traffic_allocation: c.trafficAllocation,
        params: (c.params || [])
          .filter(p => p.paramName.trim() !== "" && p.paramValue.trim() !== "")
          .map(p => ({
            param_name: p.paramName,
            param_value: p.paramValue,
          })),
      }));
    experimentData.repo_branch_pairs = (values.repoBranchPairs || [])
      .map(pair => ({ repo: pair.repo.trim(), branch: pair.branch.trim() }))
      .filter(pair => pair.repo !== "" && pair.branch !== "");
  } else {
    experimentData.imported_global_experiment_id = values.importedGlobalExperimentId
      ? parseInt(values.importedGlobalExperimentId, 10)
      : undefined;
    experimentData.import_last_synced_at = values.importLastSyncedAt || undefined;
    experimentData.scope_filters = values.scopeFilters;
    experimentData.import_metadata = values.importMetadata || undefined;
    experimentData.prediction_flights = (values.predictionFlights || [])
      .filter(pf => pf.predictionFlightId)
      .map(pf => ({
        prediction_flight_id: parseInt(pf.predictionFlightId, 10),
        variant: pf.variant,
        traffic_allocation: pf.trafficAllocation,
        params: (pf.params || [])
          .filter(p => p.predictorTypeId !== null && p.paramValue.trim() !== "")
          .map(p => ({
            predictor_type_id: p.predictorTypeId as number,
            param_value: p.paramValue,
          })),
      }));
  }

  return experimentData;
}

export function extractBackendFieldErrors(error: unknown): ExperimentFormFieldErrors | null {
  const backendErrors = (error as ErrorWithBackendValidation)?.response?.data?.errors;

  if (!backendErrors || backendErrors.length === 0) {
    return null;
  }

  const mappedFieldErrors: ExperimentFormFieldErrors = {};
  backendErrors.forEach(backendError => {
    mappedFieldErrors[backendError.field] = backendError.message;
  });

  return Object.keys(mappedFieldErrors).length > 0 ? mappedFieldErrors : null;
}
